// packages/frontend/src/utils/routeTemplate.ts
//
// Maps a concrete pathname (which may embed client, child or patient ids) to
// the matching route template from routes.tsx, so analytics can report where
// a user is in the app without ever sending an id, a query string or a hash.
// Keep this list in sync with the paths declared in ../routes.tsx.
//
// Implemented without react-router's matchPath so this stays a plain,
// dependency-free function usable from any service or util.

export const ROUTE_TEMPLATES: readonly string[] = [
  '/login',
  '/register',
  '/registration',
  '/onboarding',
  '/forgot-password',
  '/unauthorized',
  '/',
  '/clients',
  '/clients/:clientId',
  '/clients/:clientId/workspace',
  '/clients/:clientId/workspace/create-magic',
  '/clients/:clientId/workspace/sensory-book',
  '/clients/:clientId/workspace/board',
  '/clients/:clientId/images',
  '/clients/:clientId/images/create',
  '/clients/:clientId/images/edit',
  '/clients/:clientId/notes',
  '/profile',
  '/settings',
  '/settings/billing',
  '/checkout/success',
  '/checkout/cancel',
  '/demo/error-handling',
  '/debug',
  '/admin/*',
  '/404',
];

export const UNKNOWN_ROUTE_TEMPLATE = '/unknown';

function templateToRegExp(template: string): RegExp {
  const pattern = template
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) return '[^/]+';
      if (segment === '*') return '.*';
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return new RegExp(`^${pattern}$`);
}

const ROUTE_MATCHERS: ReadonlyArray<{ template: string; regex: RegExp }> =
  ROUTE_TEMPLATES.map((template) => ({ template, regex: templateToRegExp(template) }));

/**
 * Returns the route template matching the given pathname (e.g.
 * "/clients/abc123/workspace" -> "/clients/:clientId/workspace"). Query
 * strings and hashes must already be stripped by the caller. Falls back to
 * UNKNOWN_ROUTE_TEMPLATE for anything that does not match a known route,
 * rather than ever echoing the concrete path.
 */
export function getRouteTemplate(pathname: string): string {
  const match = ROUTE_MATCHERS.find(({ regex }) => regex.test(pathname));
  return match ? match.template : UNKNOWN_ROUTE_TEMPLATE;
}
